import React from 'react';
import {
  CATEGORY_L1_VALUES,
  type CategoryL1,
} from '../server/categoryTaxonomy';
import { useLanguage } from '../contexts/LanguageContext';

const CATEGORY_L1_LABELS: Record<CategoryL1, { zh: string; en: string }> = {
  models: { zh: '模型', en: 'Models' },
  apis: { zh: '接口', en: 'APIs' },
  agents: { zh: '智能体', en: 'Agents' },
  frameworks: { zh: '框架', en: 'Frameworks' },
  tools: { zh: '工具', en: 'Tools' },
  apps: { zh: '应用', en: 'Apps' },
  data: { zh: '数据', en: 'Data' },
  tutorials: { zh: '教程', en: 'Tutorials' },
  community: { zh: '社区', en: 'Community' },
  pending: { zh: '待定', en: 'Pending' },
};

interface SidebarProps {
  activeL1: 'all' | CategoryL1;
  onSelectL1: (category: 'all' | CategoryL1) => void;
  counts: Record<CategoryL1, number>;
  isOpen: boolean;
  onCloseMobile: () => void;
  onClickTitle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  activeL1,
  onSelectL1,
  counts,
  isOpen,
  onCloseMobile,
  onClickTitle
}) => {
  const { t, language } = useLanguage();
  const categories: Array<'all' | CategoryL1> = ['all', ...CATEGORY_L1_VALUES];

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-slate-900/60 z-40 lg:hidden backdrop-blur-sm transition-opacity"
          onClick={onCloseMobile}
          aria-hidden="true"
        />
      )}

      <aside
        id="app-sidebar"
        className={`
        fixed top-0 left-0 bottom-0 z-50 w-64 xl:w-72 p-6 transition-transform duration-300 lg:translate-x-0 lg:sticky lg:top-0 lg:bottom-auto lg:h-screen lg:self-start lg:flex-shrink-0
        bg-background/95 lg:bg-background/80 backdrop-blur-xl border-r border-slate-200 dark:border-white/5
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        flex flex-col
      `}
        aria-label={t.app_name}
      >
        <button
          type="button"
          onClick={onCloseMobile}
          className="lg:hidden absolute top-4 right-4 p-2 text-slate-500 dark:text-slate-300 hover:text-primary dark:hover:text-white"
          aria-label={language === 'zh' ? '关闭菜单' : 'Close menu'}
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <button
          type="button"
          onClick={() => {
            onClickTitle();
            onCloseMobile();
          }}
          aria-label={language === 'zh' ? '返回首页' : 'Back to landing page'}
          className="flex items-center gap-3 mb-10 flex-shrink-0 hover:opacity-80 transition-opacity"
        >
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-lg shadow-primary/20">
            <svg className="w-6 h-6" viewBox="0 0 64 64" fill="white">
              <path d="M16 44V20l16 12-16 12zm16-12l16-12v24l-16-12z" />
            </svg>
          </div>
          <span className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-slate-800 to-slate-500 dark:from-white dark:to-slate-400 tracking-tight">
            {t.app_name}
          </span>
        </button>

        <nav className="space-y-1.5 flex-1 overflow-y-auto min-h-0 -mx-2 px-2" aria-label={language === 'zh' ? '一级分类' : 'L1 Categories'}>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => {
                onSelectL1(cat);
                onCloseMobile();
              }}
              aria-current={activeL1 === cat ? 'page' : undefined}
              className={`
                w-full min-h-[46px] flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium transition-colors duration-300
                ${activeL1 === cat
                  ? 'bg-primary/10 text-primary ring-1 ring-primary/20 shadow-sm'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/5'}
              `}
            >
              <div className="flex items-center gap-3">
                 <span>
                  {cat === 'all'
                    ? (language === 'zh' ? '全部' : 'All')
                    : (language === 'zh' ? CATEGORY_L1_LABELS[cat].zh : CATEGORY_L1_LABELS[cat].en)}
                 </span>
              </div>
              <span className={`
                min-w-8 text-center text-[10px] px-2 py-0.5 rounded-full font-bold
                ${activeL1 === cat ? 'bg-primary text-white' : 'bg-slate-200 dark:bg-slate-800 text-slate-500'}
              `}>
                {cat === 'all' ? CATEGORY_L1_VALUES.reduce((sum, value) => sum + (counts[value] || 0), 0) : (counts[cat] || 0)}
              </span>
            </button>
          ))}
        </nav>

      </aside>
    </>
  );
};

export default React.memo(Sidebar);
